import React, { useState } from "react";
import { Crown, Swords, Skull, Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { TOP_PLAYERS, TOP_CLANS } from "../mock";
import { useTopPlayers, useTopClans } from "../hooks/useApi";

const Rankings = () => {
  const [tab, setTab] = useState("players");
  const [playerType, setPlayerType] = useState("pvp");
  const { data: playersApi, loading: lp } = useTopPlayers(playerType, 10);
  const { data: clansApi, loading: lc } = useTopClans(10);

  // Mapear datos de API al formato que usa la tabla, con fallback al mock
  const players = (playersApi?.players || TOP_PLAYERS).map((p, i) => ({
    rank: p.rank ?? i + 1,
    name: p.name,
    class: p.class,
    clan: p.clan ?? "—",
    level: p.level,
    pvp: p.pvp,
    pk: p.pk,
    online: p.online,
  }));

  const clans = (clansApi?.clans || TOP_CLANS).map((c, i) => ({
    rank: c.rank ?? i + 1,
    name: c.name,
    leader: c.leader,
    members: c.members,
    level: c.level,
    reputation: c.reputation,
    castle: c.castle ?? "—",
  }));

  return (
    <section className="relative py-28 bg-[#0a0a0d]">
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: "url(https://images.unsplash.com/photo-1773216344148-45afea590cf6?auto=format&fit=crop&w=1920&q=70)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col items-center text-center mb-12">
          <div className="ornament"><span>Salón de los Héroes</span></div>
          <h2 className="section-title mt-6">Rankings En Vivo</h2>
          <div className="gold-divider w-40 mt-6" />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex justify-center gap-1 bg-transparent p-0 h-auto mb-8">
            {[{ v: "players", l: "Top Jugadores", icon: Swords }, { v: "clans", l: "Top Clanes", icon: Crown }].map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger
                  key={t.v}
                  value={t.v}
                  className="data-[state=active]:bg-[rgba(212,168,67,0.12)] data-[state=active]:text-[#f5c542] data-[state=active]:border-[#d4a843] bg-[#14141a] border border-[rgba(212,168,67,0.15)] text-[#b8b3a8] rounded-sm px-6 py-2.5 font-display tracking-[0.18em] uppercase text-xs flex items-center gap-2"
                >
                  <Icon className="w-3.5 h-3.5" /> {t.l}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="players">
            {/* Sub-filtros de tipo */}
            <div className="flex flex-wrap justify-center gap-2 mb-5">
              {[
                { v: "pvp", l: "PvP" },
                { v: "pk", l: "PK" },
                { v: "level", l: "Level" },
                { v: "online", l: "Online Time" },
              ].map((t) => (
                <button
                  key={t.v}
                  onClick={() => setPlayerType(t.v)}
                  className={`px-4 py-1.5 rounded-sm font-display tracking-[0.18em] uppercase text-[10px] border transition-all ${
                    playerType === t.v
                      ? "bg-[rgba(212,168,67,0.15)] text-[#f5c542] border-[#d4a843]"
                      : "bg-[#14141a] text-[#b8b3a8] border-[rgba(212,168,67,0.15)] hover:text-[#f0ebe0] hover:border-[rgba(212,168,67,0.4)]"
                  }`}
                >
                  Top {t.l}
                </button>
              ))}
              {lp && (
                <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] text-[#8a8a92] font-mono">
                  <Loader2 className="w-3 h-3 animate-spin" /> cargando...
                </span>
              )}
            </div>
            <div className="glass rounded-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-[rgba(212,168,67,0.06)] border-b border-[rgba(212,168,67,0.2)]">
                  <tr className="text-left">
                    {["#", "Nombre", "Clase", "Clan", "Lvl", "PvP", "PK", ""].map((h, i) => (
                      <th key={i} className="px-4 md:px-6 py-4 font-display tracking-[0.18em] uppercase text-[10px] md:text-xs text-[#d4a843]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {players.map((p) => (
                    <tr key={p.rank} className="border-b border-[rgba(212,168,67,0.08)] hover:bg-[rgba(212,168,67,0.04)] transition-colors group">
                      <td className="px-4 md:px-6 py-3.5">
                        <RankBadge rank={p.rank} />
                      </td>
                      <td className="px-4 md:px-6 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <span className={`relative flex h-2 w-2 ${p.online ? "" : "opacity-30"}`}>
                            {p.online && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />}
                            <span className={`relative inline-flex rounded-full h-2 w-2 ${p.online ? "bg-emerald-400" : "bg-[#6f6c66]"}`} />
                          </span>
                          <span className="font-display font-semibold text-[#f0ebe0] group-hover:text-[#f5c542] transition-colors">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3.5 text-sm text-[#b8b3a8]">{p.class}</td>
                      <td className="px-4 md:px-6 py-3.5 text-sm"><span className="text-[#d4a843]">[{p.clan}]</span></td>
                      <td className="px-4 md:px-6 py-3.5 font-mono text-sm text-[#f0ebe0]">{p.level}</td>
                      <td className="px-4 md:px-6 py-3.5 font-mono text-sm text-[#10b981]">{p.pvp.toLocaleString()}</td>
                      <td className="px-4 md:px-6 py-3.5 font-mono text-sm text-[#b03030] flex items-center gap-1">
                        <Skull className="w-3 h-3" /> {p.pk}
                      </td>
                      <td className="hidden md:table-cell px-4 md:px-6 py-3.5">
                        {p.rank <= 3 && <Crown className="w-4 h-4 text-[#d4a843]" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="clans">
            <div className="glass rounded-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-[rgba(212,168,67,0.06)] border-b border-[rgba(212,168,67,0.2)]">
                  <tr className="text-left">
                    {["#", "Clan", "Líder", "Miembros", "Lvl", "Reputación", "Castle"].map((h, i) => (
                      <th key={i} className="px-4 md:px-6 py-4 font-display tracking-[0.18em] uppercase text-[10px] md:text-xs text-[#d4a843]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {clans.map((c) => (
                    <tr key={c.rank} className="border-b border-[rgba(212,168,67,0.08)] hover:bg-[rgba(212,168,67,0.04)] transition-colors group">
                      <td className="px-4 md:px-6 py-3.5"><RankBadge rank={c.rank} /></td>
                      <td className="px-4 md:px-6 py-3.5">
                        <span className="font-display font-semibold text-[#f0ebe0] group-hover:text-[#f5c542] transition-colors">[{c.name}]</span>
                      </td>
                      <td className="px-4 md:px-6 py-3.5 text-sm text-[#b8b3a8]">{c.leader}</td>
                      <td className="px-4 md:px-6 py-3.5 font-mono text-sm text-[#f0ebe0]">{c.members}</td>
                      <td className="px-4 md:px-6 py-3.5 font-mono text-sm text-[#d4a843]">{c.level}</td>
                      <td className="px-4 md:px-6 py-3.5 font-mono text-sm text-[#10b981]">{c.reputation.toLocaleString()}</td>
                      <td className="px-4 md:px-6 py-3.5 text-sm">
                        {c.castle !== "—" ? (
                          <span className="flex items-center gap-1 text-[#d4a843]"><Crown className="w-3.5 h-3.5" /> {c.castle}</span>
                        ) : (
                          <span className="text-[#6f6c66]">{c.castle}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

const RankBadge = ({ rank }) => {
  const styles = {
    1: "bg-gradient-to-br from-[#f5c542] to-[#8b6914] text-[#1a1408]",
    2: "bg-gradient-to-br from-[#c0c0c0] to-[#7d7d7d] text-[#0a0a0d]",
    3: "bg-gradient-to-br from-[#cd7f32] to-[#6b3a18] text-[#0a0a0d]",
  };
  return (
    <div className={`w-8 h-8 rounded-sm flex items-center justify-center font-display font-extrabold text-xs ${styles[rank] || "bg-[#21212b] text-[#b8b3a8]"}`}>
      {rank}
    </div>
  );
};

export default Rankings;
